import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArtifactLink } from '../../components/ArtifactLink';
import {
  ARTIFACT_TYPES,
  ARTIFACT_TYPE_LABELS,
  DATA_STATUSES,
  DATA_STATUS_LABELS,
  type ArtifactType,
  type DataStatus,
} from '@thottbot/shared';
import { apiGet, apiSend } from '../../api/client';
import { Badge } from '../../components/Badge';
import { describeError } from '../Submit';
import { ConfirmButton } from '../ModQueue';

interface Row {
  id: string;
  type: ArtifactType;
  slug: string;
  name: string;
  dataStatus: DataStatus;
  source: string;
  gameId: number | null;
  revisionNo: number;
  status: 'published' | 'removed';
  mergedInto: string | null;
  score: number;
}

export function Artifacts() {
  const qc = useQueryClient();
  const [form, setForm] = useState({ q: '', type: '', status: '', dataStatus: '' });
  const [params, setParams] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ['admin', 'artifacts', params],
    queryFn: () => apiGet<{ items: Row[]; total: number }>(`/api/admin/artifacts?${params}`),
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'artifacts'] });
    qc.invalidateQueries({ queryKey: ['artifact'] });
  };
  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      refresh();
      return true;
    } catch (err) {
      setError(describeError(err));
      return false;
    }
  };
  return (
    <div data-testid="admin-artifacts">
      <form
        className="filters"
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          const p = new URLSearchParams();
          if (form.q.trim()) p.set('q', form.q.trim());
          if (form.type) p.set('type', form.type);
          if (form.status) p.set('status', form.status);
          if (form.dataStatus) p.set('dataStatus', form.dataStatus);
          setParams(p.toString());
        }}
      >
        <input
          type="search"
          aria-label="Search artifacts"
          placeholder="name, id, or game id"
          value={form.q}
          onChange={(e) => setForm({ ...form, q: e.target.value })}
        />
        <select
          aria-label="Type filter"
          value={form.type}
          onChange={(e) => setForm({ ...form, type: e.target.value })}
        >
          <option value="">Any type</option>
          {ARTIFACT_TYPES.map((t) => (
            <option key={t} value={t}>
              {ARTIFACT_TYPE_LABELS[t].singular}
            </option>
          ))}
        </select>
        <select
          aria-label="Status filter"
          value={form.status}
          onChange={(e) => setForm({ ...form, status: e.target.value })}
        >
          <option value="">Any status</option>
          <option value="published">Published</option>
          <option value="removed">Removed</option>
        </select>
        <select
          aria-label="Data status filter"
          value={form.dataStatus}
          onChange={(e) => setForm({ ...form, dataStatus: e.target.value })}
        >
          <option value="">Any data status</option>
          {DATA_STATUSES.map((s) => (
            <option key={s} value={s}>
              {DATA_STATUS_LABELS[s]}
            </option>
          ))}
        </select>
        <button type="submit">Search</button>
      </form>
      {error && <div className="error">{error}</div>}
      {list.isPending && <p className="muted">Loading...</p>}
      {list.data && (
        <div className="table-scroll">
          <table className="grid">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Data status</th>
                <th>Rev</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((a) => (
                <ArtifactRow
                  key={a.id}
                  row={a}
                  open={open === a.id}
                  setOpen={(v) => setOpen(v ? a.id : null)}
                  act={act}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ArtifactRow({
  row,
  open,
  setOpen,
  act,
}: {
  row: Row;
  open: boolean;
  setOpen: (v: boolean) => void;
  act: (fn: () => Promise<unknown>) => Promise<boolean>;
}) {
  const [revision, setRevision] = useState('1');
  const [intoId, setIntoId] = useState('');
  const [name, setName] = useState(row.name);
  const [dataStatus, setDataStatus] = useState<DataStatus>(row.dataStatus);
  const [dataJson, setDataJson] = useState<string | null>(null);
  const [jsonError, setJsonError] = useState<string | null>(null);

  const loadData = async () => {
    const detail = await apiGet<{ artifact?: { data: Record<string, unknown> } }>(
      `/api/artifacts/${row.type}/${row.slug}`,
    );
    setDataJson(JSON.stringify(detail.artifact?.data ?? {}, null, 2));
  };

  const save = async () => {
    let data: Record<string, unknown> | undefined;
    if (dataJson !== null) {
      try {
        data = JSON.parse(dataJson) as Record<string, unknown>;
        setJsonError(null);
      } catch {
        setJsonError('Data must be valid JSON.');
        return;
      }
    }
    await act(() =>
      apiSend('PATCH', `/api/admin/artifacts/${row.id}`, {
        name,
        dataStatus,
        ...(data ? { data } : {}),
      }),
    );
  };

  return (
    <>
      <tr data-testid="artifact-row">
        <td>
          {row.status === 'published' ? (
            <ArtifactLink type={row.type} slug={row.slug} name={row.name} />
          ) : (
            <span className="muted">{row.name}</span>
          )}
          {row.gameId !== null && <span className="muted"> #{row.gameId}</span>}
        </td>
        <td>{ARTIFACT_TYPE_LABELS[row.type].singular}</td>
        <td>
          <Badge status={row.dataStatus} />
        </td>
        <td>{row.revisionNo}</td>
        <td>
          {row.status === 'removed' ? (
            <span className="error">removed{row.mergedInto ? ' (merged)' : ''}</span>
          ) : (
            'published'
          )}
        </td>
        <td>
          <button type="button" onClick={() => setOpen(!open)} aria-expanded={open}>
            {open ? 'Close' : 'Manage'}
          </button>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={6}>
            <div className="form-row">
              <label htmlFor={`name-${row.id}`}>Name</label>
              <input
                id={`name-${row.id}`}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={200}
              />
            </div>
            <div className="form-row">
              <label htmlFor={`ds-${row.id}`}>Data status</label>
              <select
                id={`ds-${row.id}`}
                value={dataStatus}
                onChange={(e) => setDataStatus(e.target.value as DataStatus)}
              >
                {DATA_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {DATA_STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-row">
              <label htmlFor={`data-${row.id}`}>Data (JSON)</label>
              {dataJson === null ? (
                <button type="button" onClick={loadData}>
                  Load data for editing
                </button>
              ) : (
                <textarea
                  id={`data-${row.id}`}
                  value={dataJson}
                  onChange={(e) => setDataJson(e.target.value)}
                  rows={10}
                  style={{ fontFamily: 'monospace' }}
                />
              )}
            </div>
            {jsonError && <div className="error">{jsonError}</div>}
            <div className="actions">
              <button type="button" onClick={save}>
                Save as new revision
              </button>
              <label>
                Revert to revision{' '}
                <input
                  type="number"
                  aria-label="Revision number"
                  min={1}
                  value={revision}
                  onChange={(e) => setRevision(e.target.value)}
                  style={{ width: 60 }}
                />
              </label>
              <ConfirmButton
                label="Revert"
                danger={false}
                onConfirm={() =>
                  act(() =>
                    apiSend('POST', `/api/admin/artifacts/${row.id}/revert`, {
                      revisionNo: Number(revision),
                    }),
                  )
                }
              />
              <label>
                Merge into id{' '}
                <input
                  type="text"
                  aria-label="Merge target id"
                  value={intoId}
                  onChange={(e) => setIntoId(e.target.value)}
                  placeholder="artifact uuid"
                  style={{ width: 280 }}
                />
              </label>
              <ConfirmButton
                label="Merge"
                onConfirm={() =>
                  act(() =>
                    apiSend('POST', `/api/admin/artifacts/${row.id}/merge`, {
                      intoId: intoId.trim(),
                    }),
                  )
                }
              />
              <ConfirmButton
                label="Delete"
                onConfirm={() => act(() => apiSend('DELETE', `/api/admin/artifacts/${row.id}`))}
              />
            </div>
            <p className="muted">
              id <code>{row.id}</code>
            </p>
          </td>
        </tr>
      )}
    </>
  );
}
